<template>
  <div>
    <div class="page-head">
      <h1>Past Quarters</h1>
      <p>Browse your institution's historical participation and results.</p>
    </div>
    <dsa-loading v-if="loading" text="Loading history…" />
    <template v-else>
      <div class="pick-row">
        <label for="subj">Subject</label>
        <select id="subj" v-model="selectedSubject" class="pick-select">
          <option disabled value="">Select a subject</option>
          <option v-for="s in subjectOptions" :key="s.subject_code" :value="s.subject_code">{{ s.subject_name }}</option>
        </select>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="!selectedSubject || attemptsLoading"
          @click="runQuery"
        >Search</button>
      </div>
      <div v-if="!subjectOptions.length" class="empty">
        <h3>No historical subjects yet</h3>
        <p>Once students register for enrolled subjects, history will appear here.</p>
      </div>
      <div v-else-if="!queried" class="empty">Select a subject and click Search to view history</div>
      <div v-else>
        <div class="subj-head">
          <h2>{{ subjectLabel(queriedSubject) }}</h2>
        </div>
        <div v-if="yearBlocks.length" class="year-chips">
          <button
            v-for="y in yearChipOptions"
            :key="y"
            type="button"
            class="yc"
            :class="{ active: !yearFilter || yearFilter === y }"
            @click="yearFilter = yearFilter === y ? '' : y"
          >{{ y }}</button>
        </div>
        <div v-for="block in filteredYearBlocks" :key="block.key" class="year-block">
          <div class="yb-head">
            <h3>{{ block.year }} · {{ block.window }} window</h3>
            <div class="meta"><b>{{ block.attempts.length }}</b> student attempts</div>
          </div>
          <table>
            <thead><tr><th>Student</th><th>Quarter</th><th>Score</th><th>Project</th><th style="text-align:right">Actions</th></tr></thead>
            <tbody>
              <tr v-for="att in block.attempts" :key="att.application_id">
                <td><span class="subject">{{ att.student_name || 'Student' }}</span></td>
                <td>{{ att.quarter_label || att.quarter_code || '—' }}</td>
                <td>
                  <span v-if="scoreText(att.score)" class="score-cell">{{ scoreText(att.score) }}</span>
                  <span v-else class="chip pend">Pending</span>
                </td>
                <td>{{ att.project_name || att.project_code || '—' }}</td>
                <td class="td-actions" style="text-align:right">
                  <button
                    v-if="att.application_id && scoreReleased(att.score)"
                    type="button"
                    class="entry"
                    @click="openTranscript(att.application_id)"
                  >Score report</button>
                  <span v-else class="nosh">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!filteredYearBlocks.length && !attemptsLoading" class="empty">No attempts recorded for this subject</div>
        <dsa-loading v-if="attemptsLoading" compact text="Loading attempts…" />
      </div>
    </template>
  </div>
</template>

<script>
import { getInstitutionSubjectsHistory, getInstitutionAttemptsBySubject } from '@/api/dsa'
import { getInstitutionAccountId } from '@/utils/store'
import { formatScoreDisplay } from '@/utils/dsaFormat'

function parseQuarterParts(code) {
  const raw = String(code || '').trim()
  const m = raw.match(/^(\d{4})\s*-?\s*(Q[1-4])$/i) || raw.match(/^(\d{4})(Q[1-4])$/i)
  if (m) {
    return { year: m[1], window: m[2].toUpperCase(), label: `${m[1]} ${m[2].toUpperCase()}` }
  }
  if (/^\d{4}$/.test(raw)) {
    return { year: raw, window: '—', label: raw }
  }
  return { year: '', window: '', label: raw || '—' }
}

export default {
  name: 'InstitutionPastQuarters',
  data() {
    return {
      loading: true,
      attemptsLoading: false,
      subjectOptions: [],
      selectedSubject: '',
      queried: false,
      queriedSubject: '',
      attempts: [],
      yearFilter: '',
    }
  },
  computed: {
    yearBlocks() {
      const map = {}
      this.attempts.forEach(att => {
        const parts = parseQuarterParts(att.quarter_code)
        const year = parts.year || '—'
        const window = parts.window || '—'
        const key = `${year}-${att.quarter_code || window || 'NA'}`
        if (!map[key]) {
          map[key] = { key, year, window, attempts: [] }
        }
        map[key].attempts.push({
          ...att,
          quarter_label: parts.label,
        })
      })
      return Object.values(map).sort((a, b) => (a.year < b.year ? 1 : -1))
    },
    yearChipOptions() {
      return [...new Set(this.yearBlocks.map(b => b.year).filter(Boolean))]
    },
    filteredYearBlocks() {
      if (!this.yearFilter) return this.yearBlocks
      return this.yearBlocks.filter(b => b.year === this.yearFilter)
    },
  },
  watch: {
    '$route.params.subjectCode'(code) {
      if (code) {
        this.selectedSubject = String(code).toUpperCase()
      }
    },
  },
  mounted() {
    const param = this.$route.params.subjectCode
    if (param) {
      this.selectedSubject = String(param).toUpperCase()
    }
    this.loadSubjects()
  },
  methods: {
    subjectLabel(code) {
      const found = this.subjectOptions.find(s => s.subject_code === code)
      return found ? found.subject_name : code
    },
    scoreText(score) {
      const view = formatScoreDisplay(score)
      if (view) return view.text
      return null
    },
    scoreReleased(score) {
      return formatScoreDisplay(score) != null
    },
    openTranscript(application_id) {
      this.$router.push(`/institution/transcript/${application_id}`)
    },
    runQuery() {
      if (!this.selectedSubject) return
      if (this.$route.params.subjectCode !== this.selectedSubject) {
        this.$router.replace(`/institution/past-quarters/${this.selectedSubject}`)
      }
      this.loadAttempts(this.selectedSubject)
    },
    async loadSubjects() {
      const account_id = getInstitutionAccountId()
      if (!account_id) return
      try {
        const res = await getInstitutionSubjectsHistory(account_id)
        this.subjectOptions = (res.data && res.data.data) || []
      } catch (e) {
        this.$emit('toast', 'Failed to load subjects')
      } finally {
        this.loading = false
      }
    },
    async loadAttempts(subject_code) {
      const account_id = getInstitutionAccountId()
      if (!account_id || !subject_code) return
      this.attemptsLoading = true
      this.queried = true
      this.queriedSubject = subject_code
      this.yearFilter = ''
      try {
        const res = await getInstitutionAttemptsBySubject(account_id, subject_code)
        this.attempts = (res.data && res.data.data && res.data.data.attempts) || []
      } catch (e) {
        this.attempts = []
        this.$emit('toast', 'Failed to load history')
      } finally {
        this.attemptsLoading = false
      }
    },
  },
}
</script>

<style scoped>
.pick-row{display:flex;align-items:center;gap:12px;margin-bottom:22px;flex-wrap:wrap}
.pick-row label{font-size:14px;font-weight:600;color:var(--ink)}
.pick-select{
  appearance:none;-webkit-appearance:none;-moz-appearance:none;
  border:1px solid #cbd5e1;border-radius:12px;padding:10px 40px 10px 16px;
  font-size:14px;font-family:inherit;background:var(--card);color:var(--ink);
  outline:none;min-width:280px;cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236b7280' d='M1.4.6L6 5.2 10.6.6 12 2 6 8 0 2z'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 14px center;
  transition:border-color .15s, box-shadow .15s;
}
.pick-select:hover{border-color:#a8b6c8}
.pick-select:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(24,95,165,.12)}
.subj-head h2{font-size:19px;font-weight:700;margin-bottom:16px}
.year-chips{display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap}
.year-chips .yc{border:1px solid #cbd5e1;border-radius:999px;padding:5px 14px;font-size:12.5px;font-weight:500;background:var(--card);color:var(--gray);cursor:pointer;font-family:inherit}
.year-chips .yc.active{background:var(--blue-light);color:var(--blue);border-color:#b9d4f2}
.year-block{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);margin-bottom:16px;overflow:hidden}
.year-block .yb-head{display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-bottom:1px solid var(--line);background:#fafbfd}
.year-block .yb-head h3{font-size:15px;font-weight:600}
.year-block .yb-head .meta{font-size:12.5px;color:var(--muted)}
.score-cell{font-weight:600;color:var(--blue)}
.nosh{color:var(--muted)}
</style>
